import { useState } from 'react';

/**
 * Custom Hook to handle wizard-like components' steps logic
 * @param {array<object>} initialStepsConfig - Array of steps to be traversed
 * @param {function} lastStepHandler - Function called when the users advances through the last step
 * @return {object} Object with the following properties:
 *  - {array<object>} steps - Steps array, with each object containing all the config/data used on it
 *  - {function} setSteps - Setter for the steps array - in case a change in order/config happens
 *  - {number} currentStep - Index of the current step
 *  - {function} setCurrentStep - Setter for the current step index - for jumps of resets
 *  - {function} addStep - Increments the current step by one
 *  - {object} previousStepData - Object used to carry over data from one step to the next after addStep
 *  - {boolean} isFinalStep - Indicates whether the current step is also the final one in the wizard
 */
const useWizardSteps = (initialStepsConfig, lastStepHandler) => {
  const [steps, setSteps] = useState(initialStepsConfig);
  const [currentStep, setCurrentStep] = useState(1);
  const [previousStepData, setPreviousStepData] = useState(null);

  const isFinalStep = currentStep === steps.length;

  /**
   * resetSteps - Resets the state to the initial values
   */
  const resetSteps = () => {
    setCurrentStep(1);
    setSteps(initialStepsConfig);
  };

  /**
   * addStep has two possible outcomes:
   * IF this is the final step, run the lastStepHandler and reset the steps state to initial
   * ELSE increment the current step by 1 and verify if it will be the last one
   * @param {object} previousStepData - Optional data object to be carried into the next step
   */
  const addStep = data => {
    if (isFinalStep) {
      // analytics will be handled by the button's custom analyticsHandler (see ModalFooter buttons)
      lastStepHandler?.({ skipAnalytics: true });
      setPreviousStepData(null);
      resetSteps();
    } else {
      setPreviousStepData(data);
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
    }
  };

  return { steps, setSteps, currentStep, setCurrentStep, addStep, isFinalStep, resetSteps, previousStepData };
};

export default useWizardSteps;
